import { Box, Container, Stack, Text, useMantineTheme } from "@mantine/core"
import { Card, Loader, LogoDark } from "@tfg/ui"
import { useEffect, useRef } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { useNavigate } from "react-router-dom"

import { LoginBackgroundIllustrations } from "~/pages/Auth/components/LoginBackgroundIllustrations"
import { LoginContent } from "~/pages/Auth/components/LoginContent"
import { useMagicLinkError } from "~/pages/Auth/hooks/useMagicLinkError"
import { useAuth } from "~/store/auth-context"

import { useLoginFlow } from "./hooks/useLoginFlow"

const publicKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY

export const Login = () => {
  const { isLoading, isAuthenticated, homeRedirectUrl } = useAuth()
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const navigate = useNavigate()

  const { clearMagicLinkError, forwardedError, forwardedErrorTitle } =
    useMagicLinkError()

  const {
    pageState,
    email,
    userId,
    canUseMagicLink,
    isLoggingIn,
    isSendingUsername,
    isSendingMagicLink,
    isSendingResetPasswordEmail,
    loginError,
    usernameError,
    magicLinkError,
    forgotPasswordError,
    handleUsernameSubmit,
    handleLogin,
    handleVerifyTwoFactor,
    handleForgotPassword,
    handleGoToForgotPassword,
    handleUseMagicLink,
    handleSendMagicLink,
    handleGoToSetPassword,
    handleBackFromMagicLink,
    handleBackToUsername,
  } = useLoginFlow()

  useEffect(() => {
    if (isAuthenticated) {
      navigate(homeRedirectUrl)
    }
  }, [isAuthenticated, navigate, homeRedirectUrl])

  const theme = useMantineTheme()

  if (isLoading) return <Loader fullPage />

  return (
    <Container
      fluid
      bg="var(--mantine-color-gray-0)"
      w="100vw"
      h="100vh"
      pos="relative"
    >
      <Box pos="absolute" top={0} left={0} right={0} bottom={0}>
        <LoginBackgroundIllustrations />
      </Box>
      <Stack mb={100} align="center" justify="center" h="100vh">
        <LogoDark />
        <Card
          px="2rem"
          py="2rem"
          h="fit-content"
          style={{ boxShadow: "none", border: "none" }}
          w={theme.other.widths.card.width.xl}
        >
          {import.meta.env.VITE_RECAPTCHA_ENABLED === "true" && (
            <ReCAPTCHA
              sitekey={publicKey}
              size="invisible"
              ref={recaptchaRef}
            />
          )}
          <Card.Section align="center" px="lg">
            <LoginContent
              pageState={pageState}
              email={email}
              userId={userId}
              isLoggingIn={isLoggingIn}
              isSendingUsername={isSendingUsername}
              isSendingMagicLink={isSendingMagicLink}
              isSendingResetPasswordEmail={isSendingResetPasswordEmail}
              loginError={loginError}
              usernameError={usernameError}
              magicLinkError={magicLinkError}
              forgotPasswordError={forgotPasswordError}
              forwardedError={forwardedError}
              forwardedErrorTitle={forwardedErrorTitle}
              onUsernameSubmit={handleUsernameSubmit}
              onClearMagicLinkError={clearMagicLinkError}
              onLogin={handleLogin}
              onVerifyTwoFactor={handleVerifyTwoFactor}
              onForgotPassword={handleForgotPassword}
              onGoToForgotPassword={handleGoToForgotPassword}
              canUseMagicLink={canUseMagicLink}
              onUseMagicLink={handleUseMagicLink}
              onSendMagicLink={handleSendMagicLink}
              onGoToSetPassword={handleGoToSetPassword}
              onBackFromMagicLink={handleBackFromMagicLink}
              onBackToUsername={handleBackToUsername}
            />
          </Card.Section>
          {import.meta.env.VITE_RECAPTCHA_ENABLED === "true" && (
            <Text style={{ textAlign: "center" }} mt="1rem">
              This site is protected by reCAPTCHA and the Google{" "}
              <a href="https://policies.google.com/privacy">Privacy Policy</a>{" "}
              and{" "}
              <a href="https://policies.google.com/terms">Terms of Service</a>{" "}
              apply.
            </Text>
          )}
        </Card>
      </Stack>
    </Container>
  )
}
